import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import styled from '@/styles';
import { GetJobQuery } from '@/types/graphql';
import { formatISO } from '@/util/date';
import TZBadge from '@/components/TZBadge';
import { axiosRubyClient } from '@/util/axios/axiosClient';

interface ShiftQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShareClick: () => void;
  job: GetJobQuery['job'];
  jobDetails?: any;
}

interface ShiftData {
  id: number | string;
  start_at: string;
  end_at: string;
  verification_code: string;
  date: string;
  formatted_date: string;
  formatted_time_range: string;
  timezone_abbr: string;
}

interface JobDetailsData {
  id: number;
  skill: {
    id: number;
    name: string;
  };
  address: {
    id: number;
    street_1: string;
    street_2: string | null;
    city: string;
    state: string;
    zip: string;
    timezone: string;
    formatted_address: string;
  };
  contact: {
    id: number;
    name: string;
    phone: string;
  };
  shifts: ShiftData[];
  shortlink?: string;
  shared_qr_verification_code?: string;
}

const HeaderDialog = styled('div', {
  padding: '20px 25px 15px 25px',
  background: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #E7E8EA',
});

const TitleDialog = styled('h2', {
  color: '#0E1E40',
  fontSize: '24px',
  fontWeight: '600',
  margin: 0,
});

const ModalContent = styled('div', {
  padding: '30px 25px 40px',
  background: 'white',
  display: 'flex',
  gap: '30px',
  alignItems: 'flex-start',

  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
});

const QRCodeContainer = styled('div', {
  flexShrink: 0,
  padding: '20px',
  border: '2px solid #E7E8EA',
  borderRadius: '12px',
  background: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const QRCodeWrapper = styled('div', {
  width: '180px',
  height: '180px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const JobDetails = styled('div', {
  flex: 1,
  minWidth: 0,
});

const Description = styled('p', {
  color: '#666',
  fontSize: '14px',
  lineHeight: '1.5',
  margin: '0 0 25px 0',
});

const JobTitle = styled('h3', {
  color: '#0E1E40',
  fontSize: '20px',
  fontWeight: '600',
  margin: '0 0 8px 0',
});

const JobId = styled('p', {
  color: '#666',
  fontSize: '14px',
  margin: '0 0 15px 0',
});

const JobLocation = styled('p', {
  color: '#0E1E40',
  fontSize: '14px',
  fontWeight: '500',
  margin: '0 0 8px 0',
});

const JobDateTime = styled('p', {
  color: '#0E1E40',
  fontSize: '14px',
  fontWeight: '500',
  margin: '0 0 20px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// Remove this styled component since we're using the existing TZBadge component

const SupervisorSection = styled('div', {
  marginTop: '20px',
  padding: '15px 0',
  borderTop: '1px solid #E7E8EA',
});

const SupervisorTitle = styled('h4', {
  color: '#0E1E40',
  fontSize: '16px',
  fontWeight: '600',
  margin: '0 0 8px 0',
});

const SupervisorName = styled('p', {
  color: '#0E1E40',
  fontSize: '14px',
  fontWeight: '500',
  margin: '0 0 4px 0',
});

const SupervisorPhone = styled('p', {
  color: '#666',
  fontSize: '14px',
  margin: '0 0 20px 0',
});

const ShiftSelectorContainer = styled('div', {
  display: 'flex',
  gap: '8px',
  marginBottom: '20px',
  flexWrap: 'wrap',
});

const ShiftButton = styled('button', {
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  border: '2px solid #E7E8EA',
  background: 'white',
  color: '#666',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&:hover': {
    borderColor: '#52C241',
    color: '#52C241',
  },

  variants: {
    active: {
      true: {
        background: '#52C241',
        borderColor: '#52C241',
        color: 'white',

        '&:hover': {
          background: '#45A735',
          borderColor: '#45A735',
          color: 'white',
        },
      },
    },
  },
});

const LoadingContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  color: '#666',
  fontSize: '14px',
});

const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '12px',
  marginTop: '20px',

  '@media (max-width: 768px)': {
    justifyContent: 'center',
  },
});

const DownloadButton = styled('button', {
  padding: '10px 20px',
  border: '1px solid #E7E8EA',
  borderRadius: '8px',
  background: 'white',
  color: '#0E1E40',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',

  '&:hover': {
    borderColor: '#ccc',
    background: '#f9f9f9',
  },
});

const ShareButton = styled('button', {
  padding: '10px 20px',
  border: '1px solid #52C241',
  borderRadius: '8px',
  background: '#52C241',
  color: 'white',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',

  '&:hover': {
    background: '#45A735',
    borderColor: '#45A735',
  },
});

const ShortlinkContainer = styled('div', {
  marginTop: '20px',
  padding: '16px',
  background: '#F7F9FC',
  borderRadius: '8px',
  border: '1px solid #E7E8EA',
});

const ShortlinkLabel = styled('p', {
  color: '#666',
  fontSize: '14px',
  margin: '0 0 8px 0',
  fontWeight: '500',
});

const ShortlinkText = styled('a', {
  color: '#52C241',
  fontSize: '14px',
  fontWeight: '500',
  wordBreak: 'break-all',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },
});

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
  </svg>
);

const ShiftQRCodeModal: React.FC<ShiftQRCodeModalProps> = ({
  isOpen,
  onClose,
  onShareClick,
  job,
  jobDetails: externalJobDetails,
}) => {
  const [jobDetails, setJobDetails] = useState<JobDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedShiftIndex, setSelectedShiftIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Process job details - either from API or passed from parent
  const processJobDetails = (jobData: any) => {
    setLoading(true);
    setError(null);

    try {
      // Check if the job has the expected shifts array structure
      if (
        !jobData.shifts ||
        !Array.isArray(jobData.shifts) ||
        jobData.shifts.length === 0
      ) {
        // Fallback: Use GraphQL job data to construct shifts if API doesn't provide them
        if (job.shifts && job.shifts.length > 0) {
          // Helper function to get timezone abbreviation
          const getTimezoneAbbr = (date: Date, timezone: string): string => {
            try {
              const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                timeZoneName: 'short',
              });
              const parts = formatter.formatToParts(date);
              const tzPart = parts.find((part) => part.type === 'timeZoneName');
              return tzPart?.value || 'EST';
            } catch {
              return 'EST';
            }
          };

          const constructedShifts = job.shifts.map((shift, index) => {
            const startDate = new Date(shift.startAt);
            const endDate = new Date(shift.endAt);
            const timezone = job.address?.timezone || 'America/New_York';

            return {
              id: shift.id,
              start_at: shift.startAt,
              end_at: shift.endAt,
              verification_code: jobData.verification_code || 'N/A',
              date: format(startDate, 'yyyy-MM-dd'),
              formatted_date: format(startDate, 'EEEE, MMMM d, yyyy'),
              formatted_time_range: `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`,
              timezone_abbr: getTimezoneAbbr(startDate, timezone),
            };
          });

          const constructedJobDetails: JobDetailsData = {
            id: jobData.id,
            skill: job.skill
              ? {
                  id: parseInt(job.skill.id),
                  name: job.skill.name,
                }
              : { id: 0, name: 'Unknown' },
            address: {
              id: job.address?.id ? parseInt(job.address.id) : 0,
              street_1: job.address?.addressLine1 || '',
              street_2: job.address?.addressLine2 || null,
              city: job.address?.city || '',
              state: job.address?.state || '',
              zip: job.address?.zip || '',
              timezone: job.address?.timezone || 'America/New_York',
              formatted_address:
                `${job.address?.addressLine1 || ''}, ${job.address?.city || ''}, ${job.address?.state || ''} ${job.address?.zip || ''}`.trim(),
            },
            contact: job.contact
              ? {
                  id: parseInt(job.contact.id),
                  name: `${job.contact.user.firstName} ${job.contact.user.lastName}`,
                  phone: job.contact.user.phoneNumber || '',
                }
              : { id: 0, name: 'Not available', phone: '' },
            shifts: constructedShifts,
            shortlink: jobData.shortlink || (job as any).shortlink,
            shared_qr_verification_code: jobData.shared_qr_verification_code,
          };

          setJobDetails(constructedJobDetails);
          setSelectedShiftIndex(0);
          setLoading(false);
          return;
        }

        setError(
          'Job shifts data is not available. Please ensure the job has shifts configured.',
        );
        setLoading(false);
        return;
      }

      setJobDetails(jobData);
      setSelectedShiftIndex(0);
      setLoading(false);
    } catch (err) {
      console.error('Error processing job details:', err);
      setError('Failed to process job details');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // If jobDetails is passed from parent, use it directly
      if (externalJobDetails) {
        processJobDetails(externalJobDetails);
      } else if (job.id) {
        // Otherwise, fetch from API
        fetchJobDetails();
      }
    }
  }, [isOpen, job.id, externalJobDetails]);

  const fetchJobDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosRubyClient.get(`/api/jobs/${job.id}`);
      if (response.data.success && response.data.data?.data) {
        const jobData = response.data.data.data;
        processJobDetails(jobData);
      } else {
        setError('Failed to load job details');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details');
      setLoading(false);
    }
  };

  const handleDownload = () => {
    // Open the shortlink in a new tab
    if (jobDetails?.shortlink) {
      window.open(jobDetails.shortlink, '_blank');
    } else {
      console.error('Shortlink is not available');
    }
  };

  if (loading || !jobDetails) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            margin: '20px',
          },
        }}
      >
        <HeaderDialog>
          <TitleDialog>Shift QR Code</TitleDialog>
          <IconButton onClick={onClose} size="small">
            <CloseOutlined />
          </IconButton>
        </HeaderDialog>
        <LoadingContainer>
          {error || 'Loading shift details...'}
        </LoadingContainer>
      </Dialog>
    );
  }

  // Handle case where shifts array might not exist or be empty
  if (!jobDetails.shifts || jobDetails.shifts.length === 0) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            margin: '20px',
          },
        }}
      >
        <HeaderDialog>
          <TitleDialog>Shift QR Code</TitleDialog>
          <IconButton onClick={onClose} size="small">
            <CloseOutlined />
          </IconButton>
        </HeaderDialog>
        <LoadingContainer>
          No shift data available for this job.
        </LoadingContainer>
      </Dialog>
    );
  }

  const selectedShift = jobDetails.shifts[selectedShiftIndex];
  const { address, skill, contact } = jobDetails;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          margin: '20px',
        },
      }}
    >
      <HeaderDialog>
        <TitleDialog>Shift QR Code</TitleDialog>
        <IconButton onClick={onClose} size="small">
          <CloseOutlined />
        </IconButton>
      </HeaderDialog>

      <ModalContent>
        <QRCodeContainer>
          <QRCodeWrapper>
            <QRCodeSVG
              id="shift-qr-code"
              value={
                jobDetails.shared_qr_verification_code ||
                selectedShift.verification_code
              }
              size={180}
              level="H"
              includeMargin={false}
            />
          </QRCodeWrapper>
        </QRCodeContainer>

        <JobDetails>
          <Description>
            Ensure your team uses the QR code to record check-in and check-out
            for their shifts.
          </Description>

          {!jobDetails.shared_qr_verification_code &&
            jobDetails.shifts.length > 1 && (
              <ShiftSelectorContainer>
                {jobDetails.shifts.map((shift, index) => (
                  <ShiftButton
                    key={shift.id}
                    active={selectedShiftIndex === index}
                    onClick={() => setSelectedShiftIndex(index)}
                  >
                    {index + 1}
                  </ShiftButton>
                ))}
              </ShiftSelectorContainer>
            )}

          <JobTitle>{skill.name}</JobTitle>
          <JobId>Job ID: {jobDetails.id}</JobId>

          <JobLocation>{address.formatted_address}</JobLocation>

          <JobDateTime>{selectedShift.formatted_date}</JobDateTime>
          <JobDateTime>
            {selectedShift.formatted_time_range}
            <span
              style={{
                background: '#E0F6DE',
                color: '#2E8722',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                marginLeft: '8px',
              }}
            >
              {selectedShift.timezone_abbr}
            </span>
          </JobDateTime>

          <SupervisorSection>
            <SupervisorTitle>Shift Supervisor</SupervisorTitle>
            <SupervisorName>{contact.name}</SupervisorName>
            <SupervisorPhone>{contact.phone}</SupervisorPhone>
          </SupervisorSection>

          {jobDetails.shortlink && (
            <ShortlinkContainer>
              <ShortlinkLabel>QR Code Download Link:</ShortlinkLabel>
              <ShortlinkText
                href={jobDetails.shortlink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {jobDetails.shortlink}
              </ShortlinkText>
            </ShortlinkContainer>
          )}

          <ButtonContainer>
            <DownloadButton onClick={handleDownload}>
              <DownloadIcon />
              Download
            </DownloadButton>
            <ShareButton onClick={onShareClick}>
              <ShareIcon />
              Share QR Code
            </ShareButton>
          </ButtonContainer>
        </JobDetails>
      </ModalContent>
    </Dialog>
  );
};

export default ShiftQRCodeModal;
